import { ArrowUpRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@highlight/ui'
import { StagedActionMarkdown, PARAGRAPH_SEPARATOR } from '@renderer/chat/integrations/staged-action'
import type { ContentFrameView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { twMerge } from 'tailwind-merge'

// Frame capabilities this client can render; a frame with any unsupported
// `requires` token falls back to plain content (see `canRenderContentFrame`).
const SUPPORTED_FRAME_CAPABILITIES = new Set(['annotated_document', 'added_content', 'removed_content', 'content'])

export function canRenderContentFrame(frame: ContentFrameView | undefined): frame is ContentFrameView {
  return frame != null && frame.requires.every((capability) => SUPPORTED_FRAME_CAPABILITIES.has(capability))
}

// Drop the structural `<annotated_document>` wrapper and put each diff marker
// on its own line, so the editor parses them as block-level regions rather than
// inline HTML stranded mid-paragraph.
const ANNOTATED_DOCUMENT_RE = /^\s*<annotated_document(?:\s[^>]*)?>([\s\S]*)<\/annotated_document>\s*$/

// One whole `<added_content>…</added_content>` / `<removed_content>…</removed_content>`
// region, body captured non-greedily (markers never nest).
const MARKER_REGION_RE = /\s*<(added_content|removed_content)((?:\s[^>]*)?)>([\s\S]*?)<\/\1>\s*/g

// Collapse each marker into a single block-level HTML element marked can parse
// cleanly. marked only treats a tag as block-level when it stands alone on its
// line, and it ends an HTML block at the first blank line — so a marker left
// inline, or one holding blank-line-separated paragraphs, gets parsed in an
// inline context with its later paragraphs stranded outside the rendered box.
// We put the tags on their own lines and fold the body to one line: paragraph
// breaks become a sentinel the NodeView re-expands, soft newlines become spaces.
function prepareFrameDocument(content: string): string {
  const inner = content.match(ANNOTATED_DOCUMENT_RE)?.[1] ?? content
  return (
    inner
      .replace(MARKER_REGION_RE, (_match, tag, attrs, body) => {
        const oneBlock = body
          .trim()
          .replace(/\n{2,}/g, PARAGRAPH_SEPARATOR)
          .replace(/\n/g, ' ')
        return `\n\n<${tag}${attrs}>\n${oneBlock}\n</${tag}>\n\n`
      })
      // Extra separator lines become visible empty paragraphs between adjacent diff regions.
      .replace(/(<\/(?:added_content|removed_content)>)\s+(?=<(?:added_content|removed_content)(?:\s|>))/g, '$1\n\n')
      .trim()
  )
}

interface ContentFrameProps {
  frame: ContentFrameView
  /** Node projected into the frame's `<content>` slot — the editor or a preview. */
  children: ReactNode
  className?: string
}

const textStyle = 'text-foreground-placeholder text-sm font-normal'

// Renders the diff document read-only, with `children` projected into its `<content>` slot.
export function ContentFrame({ frame, children, className }: ContentFrameProps) {
  const primaryButton = frame.buttons?.[0]
  const documentContent = prepareFrameDocument(frame.content)

  return (
    <div className={twMerge('bg-element overflow-hidden rounded-[10px] border p-2 pl-2.5', className)}>
      {(frame.icon || frame.title || primaryButton) && (
        <div className="mb-2 flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {frame.icon && <img src={frame.icon} alt="" className="size-3.5 shrink-0 rounded" />}
            {frame.title && <span className={twMerge('min-w-0 truncate', textStyle)}>{frame.title}</span>}
          </div>
          {primaryButton && (
            <Button
              type="button"
              shape="square"
              size="xs"
              variant="ghost"
              aria-label={primaryButton.label}
              onClick={(e) => {
                e.stopPropagation()
                window.open(primaryButton.link, '_blank')
              }}
              className="shrink-0"
            >
              <ArrowUpRight className="text-foreground-placeholder size-3.5" />
            </Button>
          )}
        </div>
      )}
      <StagedActionMarkdown
        size="sm"
        attachments={frame.attachments}
        contentSlot={children}
        className={twMerge('[&_:is(h1,h2,h3,h4,h5,h6,b,strong)]:font-normal', textStyle)}
      >
        {documentContent}
      </StagedActionMarkdown>
    </div>
  )
}
